/**
 * AK-423 — вкладка «Геометрия»: клиентская генерация зон из эталонного контура
 * и проекция геометрий в SVG. Чистые функции (без карты/DOM) — юнит-тестируемы.
 *
 * Модель/бэкенд НЕ трогаем: результат генерации POST'ится в существующий
 * /fields/{id}/subzones (field_card.py). Зоны считаются на клиенте, границы
 * потом правятся на карте инструментами рисования (отдельный эпик).
 *
 * Шаблон «Полив-круг + углы»:
 *   - центр + вписанный радиус круга — polylabel (полюс недоступности);
 *   - круг-полигон 64 вершины (в градусном пространстве, гарантированно внутри
 *     контура) = зона «Орошаемый круг»;
 *   - разность контур − круг (@turf/difference) = зона «Углы (богара)».
 * Площади — geodesic @turf/area (м² → га), корректны независимо от проекции.
 */
import polylabel from 'polylabel';
import area from '@turf/area';
import difference from '@turf/difference';
import { polygon as turfPolygon, featureCollection } from '@turf/helpers';
import type { Polygon, MultiPolygon } from 'geojson';

export interface GeoLike {
  type?: string;
  coordinates?: unknown;
}

/** Одно кольцо: [[lng, lat], …] (замкнуто — первый == последний). */
type Ring = number[][];
/** Полигон в GeoJSON-раскладке: [outerRing, ...holes]. */
type PolyCoords = Ring[];

const CIRCLE_VERTICES = 64;
// Круг чуть меньше вписанного (полюс недоступности) — чтобы гарантированно
// лежал внутри контура и «углы» имели видимую площадь.
const CIRCLE_SHRINK = 0.94;
const M2_PER_HA = 10_000;

// ─── Разбор GeoJSON контура ─────────────────────────────────────────────

function toRing(raw: unknown): Ring | null {
  if (!Array.isArray(raw)) return null;
  const pts = raw
    .filter(
      (c): c is number[] =>
        Array.isArray(c) &&
        c.length >= 2 &&
        typeof c[0] === 'number' &&
        typeof c[1] === 'number',
    )
    .map((c) => [c[0], c[1]] as number[]);
  if (pts.length < 3) return null;
  // Замкнуть, если не замкнуто (turf/postgis требуют замкнутое кольцо).
  const first = pts[0];
  const last = pts[pts.length - 1];
  if (first[0] !== last[0] || first[1] !== last[1]) pts.push([first[0], first[1]]);
  return pts;
}

function toPoly(raw: unknown): PolyCoords | null {
  if (!Array.isArray(raw)) return null;
  const rings = raw.map(toRing).filter((r): r is Ring => r != null);
  return rings.length ? rings : null;
}

/** Разворачивает Polygon / MultiPolygon в список полигонов (каждый — outer + holes). */
export function extractPolygons(geo?: GeoLike | null): PolyCoords[] {
  if (!geo || !geo.coordinates) return [];
  const out: PolyCoords[] = [];
  if (geo.type === 'Polygon') {
    const p = toPoly(geo.coordinates);
    if (p) out.push(p);
  } else if (geo.type === 'MultiPolygon') {
    for (const poly of geo.coordinates as unknown[]) {
      const p = toPoly(poly);
      if (p) out.push(p);
    }
  }
  return out;
}

/** Абсолютная площадь кольца в градусном пространстве (шнуровка) — для выбора крупнейшего полигона. */
function ringAreaAbs(ring: Ring): number {
  let a = 0;
  for (let i = 0, n = ring.length, j = n - 1; i < n; j = i++) {
    a += (ring[j][0] + ring[i][0]) * (ring[j][1] - ring[i][1]);
  }
  return Math.abs(a / 2);
}

/** Крупнейший полигон MultiPolygon'а (по площади внешнего кольца). */
export function largestPolygon(geo?: GeoLike | null): PolyCoords | null {
  const polys = extractPolygons(geo);
  if (!polys.length) return null;
  return polys.reduce((best, p) =>
    ringAreaAbs(p[0]) > ringAreaAbs(best[0]) ? p : best,
  );
}

/** Площадь контура (га) — geodesic. null, если геометрию не разобрать. */
export function contourAreaHa(geo?: GeoLike | null): number | null {
  const polys = extractPolygons(geo);
  if (!polys.length) return null;
  try {
    const fc = featureCollection(polys.map((p) => turfPolygon(p)));
    return area(fc) / M2_PER_HA;
  } catch {
    return null;
  }
}

// ─── Шаблон «Полив-круг + углы» ─────────────────────────────────────────

export interface PivotSplit {
  /** Круг-полигон (64 вершины) — зона «Орошаемый круг». */
  circle: Polygon;
  /** Контур − круг — зона «Углы (богара)» (полигон с дыркой / мультиполигон). */
  corners: Polygon | MultiPolygon;
  circleAreaHa: number;
  cornersAreaHa: number;
}

function precisionFor(ring: Ring): number {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [x, y] of ring) {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  const span = Math.max(maxX - minX, maxY - minY);
  return Math.max(span / 500, 1e-7);
}

/**
 * Строит зоны «Орошаемый круг» + «Углы (богара)» из эталонного контура.
 * Возвращает null, если контур не разобрать или вписанный круг вырожден.
 */
export function buildPivotSplit(contour?: GeoLike | null): PivotSplit | null {
  const poly = largestPolygon(contour);
  if (!poly) return null;

  const center = polylabel(poly, precisionFor(poly[0]));
  const [cx, cy] = center;
  const rDeg = center.distance * CIRCLE_SHRINK;
  if (!(rDeg > 0)) return null;

  const ring: number[][] = [];
  for (let i = 0; i < CIRCLE_VERTICES; i++) {
    const t = (2 * Math.PI * i) / CIRCLE_VERTICES;
    ring.push([cx + rDeg * Math.cos(t), cy + rDeg * Math.sin(t)]);
  }
  ring.push([ring[0][0], ring[0][1]]); // замкнуть

  const circleGeom: Polygon = { type: 'Polygon', coordinates: [ring] };

  const contourFeat = turfPolygon(poly);
  const circleFeat = turfPolygon([ring]);
  const diff = difference(featureCollection([contourFeat, circleFeat]));

  const cornersGeom = (diff?.geometry ?? contourFeat.geometry) as
    | Polygon
    | MultiPolygon;

  return {
    circle: circleGeom,
    corners: cornersGeom,
    circleAreaHa: area(circleFeat) / M2_PER_HA,
    cornersAreaHa: diff ? area(diff) / M2_PER_HA : 0,
  };
}

// ─── Проекция нескольких геометрий в общий SVG viewBox ──────────────────

export interface XY {
  x: number;
  y: number;
}

export interface ProjectedLayers {
  size: number;
  /** По одному элементу на входную геометрию (в том же порядке); [] если геометрия пуста. */
  layers: XY[][][];
}

/**
 * Проецирует набор геометрий в ОДИН квадратный viewBox size×size с общими
 * границами (чтобы контур и зоны совпадали). Долгота → x, широта → y (инверт.).
 * null, если рисовать нечего.
 */
export function projectLayers(
  geos: Array<GeoLike | null | undefined>,
  size = 100,
  pad = 6,
): ProjectedLayers | null {
  const perGeoRings: Ring[][] = geos.map((g) =>
    extractPolygons(g).flatMap((poly) => poly),
  );

  let minLon = Infinity;
  let maxLon = -Infinity;
  let minLat = Infinity;
  let maxLat = -Infinity;
  for (const rings of perGeoRings) {
    for (const ring of rings) {
      for (const [lon, lat] of ring) {
        if (lon < minLon) minLon = lon;
        if (lon > maxLon) maxLon = lon;
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
      }
    }
  }
  if (!isFinite(minLon) || !isFinite(minLat)) return null;

  const spanLon = maxLon - minLon;
  const spanLat = maxLat - minLat;
  const inner = size - 2 * pad;
  const project = (lon: number, lat: number): XY => ({
    x: spanLon > 0 ? pad + ((lon - minLon) / spanLon) * inner : size / 2,
    y: spanLat > 0 ? pad + ((maxLat - lat) / spanLat) * inner : size / 2,
  });

  const layers = perGeoRings.map((rings) =>
    rings.map((ring) => ring.map(([lon, lat]) => project(lon, lat))),
  );
  return { size, layers };
}

// ─── Цвета зон — semantic-токены (hex запрещён вне whitelist) ────────────

export interface ZoneVisual {
  /** Класс заливки маркера в списке (bg-*). */
  bg: string;
  /** Класс цвета для SVG (fill/stroke = currentColor). */
  text: string;
}

// Круг/богара — фирменные (зелёный/янтарь, как в эталоне founder); прочие
// виды — chart-токены по кругу. Всё semantic, ни одного hex.
const KIND_VISUAL: Record<string, ZoneVisual> = {
  irrigated_pivot: { bg: 'bg-primary', text: 'text-primary' },
  dryland_corner: { bg: 'bg-accent-brand', text: 'text-accent-brand' },
};
const CHART_CYCLE: ZoneVisual[] = [
  { bg: 'bg-chart-3', text: 'text-chart-3' },
  { bg: 'bg-chart-4', text: 'text-chart-4' },
  { bg: 'bg-chart-5', text: 'text-chart-5' },
  { bg: 'bg-chart-1', text: 'text-chart-1' },
  { bg: 'bg-chart-2', text: 'text-chart-2' },
];

/** Цвет зоны по её виду; для неизвестных видов — циклично из chart-палитры. */
export function zoneVisual(kind: string, index: number): ZoneVisual {
  return KIND_VISUAL[kind] ?? CHART_CYCLE[index % CHART_CYCLE.length];
}

/** SVG-path из спроецированного кольца. */
export function ringToPath(ring: XY[]): string {
  if (!ring.length) return '';
  return (
    ring
      .map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
      .join(' ') + ' Z'
  );
}

/** Русское склонение: 1 зона · 2 зоны · 5 зон. */
export function pluralZones(n: number): string {
  const mod100 = Math.abs(n) % 100;
  const mod10 = mod100 % 10;
  if (mod100 >= 11 && mod100 <= 14) return `${n} зон`;
  if (mod10 === 1) return `${n} зона`;
  if (mod10 >= 2 && mod10 <= 4) return `${n} зоны`;
  return `${n} зон`;
}
